import {
  QuestionCheckboxModel,
  QuestionPanelDynamicModel,
  SurveyModel,
} from "survey-core";

/**
 * Generates one dynamic-panel entry per sub-type the respondent selected in a
 * driving checkbox question, so a repeated block (e.g. a production-output
 * table) appears once per selected item instead of once per category.
 *
 * WHY THIS EXISTS: SurveyJS has no native "one panel per selected choice"
 * binding. Hardcoding one block per possible sub-type would put every block in
 * the schema (45 for CRP non-textile) and instantiate every nested matrix on
 * page load, and it still could not title a block with free text the
 * respondent types into "Other". So the panel list is reconciled in code.
 *
 * WHY IT IS KEYED OFF A CUSTOM SCHEMA PROPERTY: the backend rewrites schema
 * expressions (question name -> injected valueName) when it serves a
 * questionnaire, which makes expression-based bindings fragile. The sanctioned
 * pattern in this codebase is imperative code reading a custom property -
 * the same reason `lockInitialPanels` exists rather than an `enableIf`.
 *
 * @see src/register-properties.ts - the `subTypeSourceQuestion` property
 * @see schemas/crp-non-textile-schema.json - production output panels
 * @see src/utils/bom-utils.ts - the same diff-and-preserve idea for BOM suppliers
 */

/** Value SurveyJS stores in the answer array when "Other" is ticked. */
const OTHER_VALUE = "other";

/**
 * Prefix of the per-category opt-out choices (`NO_FOOD_AND_BEVERAGE`, ...).
 * Selecting one means "we do not make these", so it generates no panel.
 */
const NO_SELECTION_PREFIX = "NO_";

/** Suffix SurveyJS appends to the data key holding "Other" free text. */
export const OTHER_COMMENT_SUFFIX = "-Comment";

/** Suffix of the hidden template field holding a panel's sub-type identity. */
const ITEM_FIELD_SUFFIX = "-item";

/** Suffix of the hidden template field recording where the identity came from. */
const ORIGIN_FIELD_SUFFIX = "-item-origin";

export type SubTypeOrigin = "choice" | "other";

export interface SubTypeItem {
  /**
   * For a `choice` item, the choice value (e.g. `JUICES`) - the hidden dropdown
   * renders its translated title as the panel heading. For an `other` item, the
   * free text itself, which has no matching choice and so renders verbatim.
   */
  key: string;
  origin: SubTypeOrigin;
}

type PanelEntry = Record<string, unknown>;

/**
 * Turns a checkbox selection into the ordered list of panels that should exist.
 *
 * Ordering follows `choiceOrder`, NOT the selection array: SurveyJS stores
 * checkbox values in the order the respondent clicked them, while the ticket
 * requires choice-list order. Unknown values keep their relative order at the
 * end, and the "Other" entry always sorts last.
 */
export const deriveSubTypeItems = (
  selected: unknown,
  otherText: unknown,
  choiceOrder: string[]
): SubTypeItem[] => {
  if (!Array.isArray(selected)) return [];

  const rank = new Map(choiceOrder.map((value, index) => [value, index]));

  const chosen = selected
    .filter(
      (value): value is string =>
        typeof value === "string" &&
        value !== OTHER_VALUE &&
        !value.startsWith(NO_SELECTION_PREFIX)
    )
    .map((value, index) => ({ value, index }))
    .sort((a, b) => {
      const rankA = rank.get(a.value) ?? Number.MAX_SAFE_INTEGER;
      const rankB = rank.get(b.value) ?? Number.MAX_SAFE_INTEGER;
      return rankA === rankB ? a.index - b.index : rankA - rankB;
    })
    .map(({ value }): SubTypeItem => ({ key: value, origin: "choice" }));

  if (!selected.includes(OTHER_VALUE)) return chosen;

  const trimmed = typeof otherText === "string" ? otherText.trim() : "";
  // An empty "Other" box has nothing to title a panel with, so generate none.
  if (!trimmed) return chosen;

  return [...chosen, { key: trimmed, origin: "other" }];
};

export interface SubTypePanelSyncPlan {
  /** The panel array the dynamic panel should hold. */
  nextValue: PanelEntry[];
  /**
   * Indices into `previous` that no longer have a matching selection, sorted
   * descending so a caller can delete them without shifting later indices.
   */
  removedIndices: number[];
  changed: boolean;
}

/**
 * Diffs the panels that exist against the panels that should exist, preserving
 * each surviving panel's answers.
 *
 * Matching is by identity where identity exists: a `choice` panel matches on
 * its item key, and the single `other` panel matches on its origin so that
 * editing the free text renames the panel instead of discarding the data behind
 * it. Entries carrying no identity at all are adopted by position - see
 * `isTagged`.
 */
export const planSubTypePanelSync = (
  itemField: string,
  originField: string,
  previous: unknown,
  target: SubTypeItem[]
): SubTypePanelSyncPlan => {
  const entries: PanelEntry[] = Array.isArray(previous)
    ? previous.filter((entry): entry is PanelEntry => !!entry && typeof entry === "object")
    : [];

  const claimed = new Set<number>();

  /**
   * True when an entry carries a hidden identity field, so it can be matched by
   * sub-type rather than by position.
   *
   * WHY THIS DISTINCTION EXISTS: answers restored from the backend carry no
   * identity. The backend builds the panel array positionally - a matrix cell
   * save sends `parentGroupIndex`, so the stored group holds the matrix value
   * and nothing else - and nothing persists the hidden `-item`/`-item-origin`
   * fields. Identity matching therefore matched nothing on load, and every
   * restored table was replaced with an empty one.
   *
   * @see onMatrixCellValueChanging in src/pages/survey-page.tsx - the save path
   */
  const isTagged = (entry: PanelEntry): boolean =>
    entry[itemField] !== undefined || entry[originField] !== undefined;

  const findMatch = (item: SubTypeItem): number => {
    const byIdentity = entries.findIndex((entry, index) => {
      if (claimed.has(index) || !isTagged(entry)) return false;
      const origin = entry[originField];
      if (item.origin === "other") return origin === "other";
      // Entries written before this field existed are treated as choices.
      return origin !== "other" && entry[itemField] === item.key;
    });
    if (byIdentity !== -1) return byIdentity;

    // No identity to match on. Untagged entries are never claimed by the pass
    // above, so handing the earliest unclaimed one to the earliest unmatched
    // item restores the order the backend array was written in - which is this
    // same derived order.
    return entries.findIndex(
      (entry, index) => !claimed.has(index) && !isTagged(entry)
    );
  };

  const nextValue = target.map((item) => {
    const matchIndex = findMatch(item);
    if (matchIndex === -1) {
      return { [itemField]: item.key, [originField]: item.origin };
    }
    claimed.add(matchIndex);
    // Keep the panel's answers; only its identity fields are authoritative.
    return {
      ...entries[matchIndex],
      [itemField]: item.key,
      [originField]: item.origin,
    };
  });

  const removedIndices = entries
    .map((_, index) => index)
    .filter((index) => !claimed.has(index))
    .sort((a, b) => b - a);

  const changed =
    removedIndices.length > 0 ||
    entries.length !== nextValue.length ||
    JSON.stringify(entries) !== JSON.stringify(nextValue);

  return { nextValue, removedIndices, changed };
};

/**
 * Resolves the data key of a template field. The backend injects a distinct
 * valueName per element and keys answers by it, so a panel entry's keys are
 * valueNames rather than element names.
 *
 * @see buildDynamicPanelAnswerValue in src/utils/index.ts - same resolution
 */
const resolveFieldKey = (
  question: QuestionPanelDynamicModel,
  name: string
): string => question.template.getQuestionByName(name)?.getValueName() ?? name;

/**
 * Locates the checkbox that drives a dynamic panel.
 *
 * Matched with `includes` rather than equality to match the convention used
 * elsewhere for cross-question lookups, which tolerates the backend decorating
 * question names.
 */
const findSourceQuestion = (
  survey: SurveyModel,
  sourceName: string
): QuestionCheckboxModel | undefined =>
  survey
    .getAllQuestions()
    .find((question) => question.name.includes(sourceName)) as
    | QuestionCheckboxModel
    | undefined;

export interface SubTypePanelSyncOptions {
  /**
   * Called for each dropped panel before the value is replaced, so the backend
   * can delete the stored group.
   *
   * WHY THIS IS NEEDED: assigning `question.value` skips
   * `onDynamicPanelRemoving`, so the removal the manual remove button performs
   * (`apiService.removeGroupByIndex`) never happens on its own and the group
   * would be orphaned server-side.
   */
  removeGroup?: (valueName: string, panelIndex: number) => void;
}

/**
 * Brings every sub-type-driven dynamic panel in the survey in line with its
 * driving checkbox. Safe to call on any value change: it assigns nothing when
 * the panel list already matches, so it cannot loop.
 */
export const syncSubTypePanels = (
  survey: SurveyModel,
  { removeGroup }: SubTypePanelSyncOptions = {}
): void => {
  survey.getAllQuestions().forEach((question) => {
    if (question.getType() !== "paneldynamic") return;

    const sourceName = question.getPropertyValue("subTypeSourceQuestion");
    if (!sourceName || typeof sourceName !== "string") return;

    const panelQuestion = question as QuestionPanelDynamicModel;
    const source = findSourceQuestion(survey, sourceName);
    if (!source) return;

    const target = deriveSubTypeItems(
      source.value,
      source.comment,
      (source.choices ?? []).map((choice) => choice.value)
    );

    const itemField = resolveFieldKey(
      panelQuestion,
      `${sourceName}${ITEM_FIELD_SUFFIX}`
    );
    const originField = resolveFieldKey(
      panelQuestion,
      `${sourceName}${ORIGIN_FIELD_SUFFIX}`
    );

    const { nextValue, removedIndices, changed } = planSubTypePanelSync(
      itemField,
      originField,
      panelQuestion.value,
      target
    );

    if (!changed) return;

    if (removeGroup) {
      const valueName = panelQuestion.getValueName();
      // Descending, so each deletion leaves the remaining indices valid.
      removedIndices.forEach((index) => removeGroup(valueName, index));
    }

    panelQuestion.value = nextValue;

    // An invisible dynamic panel does not rebuild its panels when its value is
    // replaced (survey-core only reconciles panels for a rendered question), so
    // clearing the list of a hidden panel would leave stale, blank-titled panel
    // objects behind. Setting panelCount collapses them.
    if (nextValue.length === 0) {
      panelQuestion.panelCount = 0;
    }
  });
};

/**
 * True when a value change could affect a sub-type panel list - either a
 * driving checkbox itself or the free text behind its "Other" option, whose
 * data key is the question name plus `-Comment`.
 *
 * Used only to avoid walking every question on unrelated changes (such as
 * typing in a matrix cell); `syncSubTypePanels` is idempotent, so a false
 * positive costs a wasted pass and nothing more.
 */
export const isSubTypeSourceChange = (
  survey: SurveyModel,
  changedName: string
): boolean =>
  survey
    .getAllQuestions()
    .filter((question) => question.getType() === "paneldynamic")
    .map((question) => question.getPropertyValue("subTypeSourceQuestion"))
    .some(
      (name) =>
        !!name && typeof name === "string" && changedName.includes(name)
    );
